/**
 * Items that got better or worse against the reference, by the item's mean
 * score (a change is five points or more). Each value opens that experiment's
 * item panel. Only shown when every experiment ran the same dataset version.
 */

import { Link } from "react-router";
import { Segmented } from "@/components/ui/tabs";
import { EmptyState } from "@/components/ui/states";
import type { ExperimentSummary } from "@/features/experiments/types";
import { formatScore } from "@/lib/format";
import { ITEM_CHANGE, itemDirection, keepItem, signed, toneOf, type ItemFilter } from "./metrics";
import { SeriesLabel, TONE_CLASS } from "./series-label";
import type { Comparison, ItemChange } from "./types";

const SHOWN = 200;

function Outcome({
  change,
  experiment,
  refId,
}: {
  change: ItemChange;
  experiment: ExperimentSummary;
  refId: string;
}) {
  const r = change.results[experiment.id];
  if (!r) return <td className="text-muted">—</td>;
  const base = change.results[refId]?.mean;
  const diff =
    experiment.id !== refId && r.mean !== null && base !== null && base !== undefined ? r.mean - base : null;
  const text =
    r.status === "done"
      ? formatScore(r.mean)
      : r.status === "agent_error"
        ? "failed"
        : r.status.replace("_", " ");
  return (
    <td>
      <Link
        to={`/experiments/${experiment.id}?item=${r.item_result_id}`}
        className="inline-flex items-baseline gap-1.5 font-mono hover:underline"
        aria-label={`Item ${change.position + 1} in ${experiment.name}: ${text}`}
      >
        <span className="font-semibold">{text}</span>
        {diff !== null && (
          <span className={`text-[11px] ${TONE_CLASS[toneOf(diff, "high", ITEM_CHANGE - 1e-9)]}`}>
            {signed(diff, (a) => a.toFixed(2))}
          </span>
        )}
      </Link>
    </td>
  );
}

export function CompareItems({
  data,
  refId,
  filter,
  onFilter,
  evaluator,
  vs,
  onClearScope,
}: {
  data: Comparison;
  refId: string;
  filter: ItemFilter;
  onFilter: (f: ItemFilter) => void;
  /** Judge changes on this evaluator only (and against this experiment only). */
  evaluator?: string | null;
  vs?: string | null;
  onClearScope?: () => void;
}) {
  if (!data.same_dataset_version) {
    return (
      <EmptyState title="Items do not line up">
        These experiments ran on different dataset versions, so their items cannot be matched one to one. The
        scores and config still compare.
      </EmptyState>
    );
  }
  const all = data.item_changes;
  const scope = { evaluator, vs };
  const dirs = all.map((c) => itemDirection(c, refId, scope));
  const count = (k: "changed" | "worse" | "better") => dirs.filter((d) => d[k]).length;
  const rows = all.filter((c) => keepItem(c, refId, filter, scope));
  const refName = data.experiments.find((e) => e.id === refId)?.name ?? "";

  return (
    <>
      <div className="flex flex-wrap items-center gap-2.5">
        <Segmented
          label="Filter items"
          value={filter}
          onChange={onFilter}
          items={[
            { key: "all", label: "All", count: all.length },
            { key: "changed", label: "Changed", count: count("changed") },
            { key: "worse", label: "Got worse", count: count("worse") },
            { key: "better", label: "Got better", count: count("better") },
          ]}
        />
        <span className="text-xs text-muted">
          Against {refName} · a change is 5 points or more on the item's{" "}
          {evaluator ? "score for this evaluator" : "mean score"}
        </span>
        {evaluator && (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-well px-2 py-1 text-xs">
            {data.evaluators.find((e) => e.key === evaluator)?.name ?? evaluator}
            {vs && ` · ${data.experiments.find((e) => e.id === vs)?.name ?? ""}`}
            <button type="button" className="link" onClick={onClearScope}>
              Clear
            </button>
          </span>
        )}
      </div>
      <section className="box" aria-label="Item changes">
        {rows.length === 0 ? (
          <EmptyState title="No items match this filter." />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Input</th>
                  {data.experiments.map((e, i) => (
                    <th key={e.id}>
                      <SeriesLabel experiment={e} index={i} isRef={e.id === refId} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, SHOWN).map((c) => (
                  <tr key={c.dataset_item_id}>
                    <td className="font-mono text-xs text-muted">{c.position + 1}</td>
                    <td className="max-w-[360px]">
                      <span className="clamp-2 text-[13px]">{c.input_preview}</span>
                      <span className="clamp-2 text-xs text-muted">
                        Expected: {c.expected_preview || "—"}
                      </span>
                    </td>
                    {data.experiments.map((e) => (
                      <Outcome key={e.id} change={c} experiment={e} refId={refId} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-line-soft bg-well px-4 py-2.5 text-xs text-muted">
          Showing {Math.min(rows.length, SHOWN)} of {rows.length} items
        </div>
      </section>
    </>
  );
}
